import { RevisionSingleActionProps } from 'components/tables/data/revision/types';
import { Loader, Menu, Tooltip } from '@mantine/core';
import { IconPrinter } from '@tabler/icons-react';
import { useFileManager } from 'api/file-manager/file-manager-context';
import panic from 'errors/panic';
import { useState } from 'react';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

/**
 * Opens the system print dialog for a given PDF blob.
 */
function printPdfBlob(blob: Blob) {
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;

  iframe.onload = () => {
    iframe.contentWindow?.print();
    // Clean up after a delay to allow the print dialog to open
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 1000);
  };

  document.body.appendChild(iframe);
}

/**
 * Action to print the revision PDF. If the revision has an archive clause,
 * it first concatenates the main PDF with the archive clause PDF.
 */
export default function PrintPdfAction({ revision, kind }: RevisionSingleActionProps) {
  const { readFile, concatPdf } = useFileManager();
  const [loading, setLoading] = useState(false);

  /**
   * Fetches the PDF (concatenating with archive clause if needed) and opens the print dialog.
   */
  const handlePrint = async () => {
    if (!revision.fileId) {
      return;
    }

    setLoading(true);
    try {
      let fileIdToPrint = revision.fileId;

      if (revision.archiveClauseFileId) {
        fileIdToPrint = await concatPdf({
          fileName: `print-${revision.revisionId}`,
          fileIds: [revision.fileId, revision.archiveClauseFileId],
        });
      }

      const blob = await readFile({ fileId: fileIdToPrint });
      printPdfBlob(blob);
    } catch (error: any) {
      panic(error);
    } finally {
      setLoading(false);
    }
  };

  if (kind === 'action-icon') {
    return (
      <Tooltip label="Tlačiť PDF" events={{ hover: true, focus: true, touch: true }}>
        <ActionIcon variant="secondary" size="md" onClick={handlePrint} loading={loading}>
          {loading ? <Loader size={24} /> : <IconPrinter stroke={1.5} size={24} />}
        </ActionIcon>
      </Tooltip>
    );
  }

  if (kind === 'button') {
    return (
      <Button
        size="md"
        onClick={handlePrint}
        leftIcon={loading ? <Loader size={24} /> : <IconPrinter stroke={1.5} size={24} />}
        variant="secondary"
        w={188}
        loading={loading}
      >
        Tlačiť PDF
      </Button>
    );
  }

  return (
    <Menu.Item
      color="gray.8"
      onClick={handlePrint}
      icon={loading ? <Loader size={24} /> : <IconPrinter stroke={1.5} size={24} />}
      disabled={loading}
    >
      Tlačiť PDF
    </Menu.Item>
  );
}
